// -*- coding: utf-8 -*-
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Assignment from '@mui/icons-material/Assignment';
import BarChart from '@mui/icons-material/BarChart';
import PeopleAlt from '@mui/icons-material/PeopleAlt';
import Chat from '@mui/icons-material/Chat';
import ClassIcon from '@mui/icons-material/Class';
import Person from '@mui/icons-material/Person';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import SchoolIcon from '@mui/icons-material/School';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InsightsIcon from '@mui/icons-material/Insights';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TimelineIcon from '@mui/icons-material/Timeline';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import RateReviewIcon from '@mui/icons-material/RateReview';
import FlashOn from '@mui/icons-material/FlashOn';
import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
import AssignmentTurnedIn from '@mui/icons-material/AssignmentTurnedIn';
import FactCheck from '@mui/icons-material/FactCheck';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import { isFeatureVisible } from './featureVisibility';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

// Each dashboard tab has a stable URL so it can be opened directly and the
// browser back/forward buttons retain the selected page.
export const getAdminMenuPath = (key) => {
  if (key === 'genelDurum') return '/admin';
  const segment = String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();
  return `/admin/${segment}`;
};

const withDefaultPath = (item) => {
  if (item.path || !Number.isInteger(item.index) || item.index < 0) {
    return item;
  }
  return { ...item, path: getAdminMenuPath(item.key) };
};

// Navigation structure mapping to Dashboard tab indices and URLs.
export const buildGroups = (
  isTeacher,
  allowTeacherDenemePlanlama = false,
  featureSettings = {},
  viewerRole = isTeacher ? 'teacher' : 'admin'
) => {
  const applyVisibility = (item, featureKey = item?.key) => ({
    ...item,
    hidden: Boolean(item?.hidden) || !isFeatureVisible(featureSettings, featureKey, viewerRole),
  });
  const canAccessDenemePlanlama = !isTeacher || allowTeacherDenemePlanlama;

  const items = {
    ogretmenAnasayfa: { key: 'ogretmenAnasayfa', label: 'Anasayfa', icon: <DashboardIcon />, index: 49, hidden: false },
    genelDurum: applyVisibility({ key: 'genelDurum', label: 'Genel Durum', icon: <DashboardIcon />, index: 0, hidden: isTeacher }),
    devamsizlikYonetimi: applyVisibility({ key: 'devamsizlikYonetimi', label: 'Devamsızlık Yönetimi', icon: <Assignment />, index: 1 }),
    devamsizlikDurumu: applyVisibility({
      key: 'devamsizlikDurumu',
      label: 'Devamsızlık Durumu',
      icon: <BarChart />,
      index: 2,
      hidden: isTeacher,
    }),
    beslenmeYonetimi: applyVisibility({ key: 'beslenmeYonetimi', label: 'Beslenme Değerlendirme', icon: <FactCheck />, index: 39 }),
    beslenmeDurumu: applyVisibility({ key: 'beslenmeDurumu', label: 'Beslenme Durumu', icon: <BarChart />, index: 40, hidden: isTeacher }),
    materyalYonetimi: applyVisibility({ key: 'materyalYonetimi', label: 'Öğrenci Materyalleri', icon: <FactCheck />, index: 47 }),
    materyalDurumu: applyVisibility({ key: 'materyalDurumu', label: 'Materyal Durumu', icon: <BarChart />, index: 48, hidden: isTeacher }),
    ogrenmeCiktisiDegerlendirme: applyVisibility({ key: 'ogrenmeCiktisiDegerlendirme', label: 'Öğrenme Çıktısı Değerlendirme', icon: <AssessmentIcon />, index: 41 }),
    ogrenmeCiktisiOzet: applyVisibility({ key: 'ogrenmeCiktisiOzet', label: 'Öğrenme Çıktısı Özeti', icon: <InsightsIcon />, index: 42 }),
    ogrenmeCiktisiOgrenciPerformans: applyVisibility({ key: 'ogrenmeCiktisiOgrenciPerformans', label: 'Öğrenci Performansı', icon: <BarChart />, index: 43 }),
    veliOgrenci: applyVisibility({ key: 'veliOgrenci', label: 'Veli-Öğrenci Yönetimi', icon: <PeopleAlt />, index: 3 }),
    veliTanisma: { key: 'veliTanisma', label: 'Veli Tanışma Talepleri', icon: <PeopleAlt />, index: 52, hidden: isTeacher },
    sinif: applyVisibility({ key: 'sinif', label: isTeacher ? 'Atandığım Şubeler' : 'Şube Yönetimi', icon: <ClassIcon />, index: 4 }),
    ders: applyVisibility({ key: 'ders', label: 'Ders Yönetimi', icon: <MenuBookIcon />, index: 5, hidden: isTeacher }),
    // Payments split: plan & statistics (new group ödeme)
    odemePlani: applyVisibility({ key: 'odemePlani', label: 'Ödeme Planı', icon: <ReceiptLongIcon />, index: 13, hidden: isTeacher }),
    danduOdemeleri: applyVisibility({ key: 'danduOdemeleri', label: 'DANDU Üyelikleri', icon: <ReceiptLongIcon />, index: 51, hidden: isTeacher }),
    odemeIstatistik: applyVisibility({ key: 'odemeIstatistik', label: 'Ödeme İstatistikleri', icon: <InsightsIcon />, index: 14, hidden: isTeacher }),
    dersProgrami: applyVisibility({ key: 'dersProgrami', label: 'Ders Programı', icon: <ScheduleIcon />, index: 6 }),
    odev: applyVisibility({ key: 'odev', label: 'Ödev Yönetimi', icon: <AssignmentTurnedIn />, index: 12 }),
    odevDurumlari: applyVisibility({ key: 'odevDurumlari', label: 'Ödev Durumları', icon: <FactCheck />, index: 20 }),
    odevDurumuOzeti: applyVisibility({ key: 'odevDurumuOzeti', label: 'Ödev Durumu Özeti', icon: <FactCheck />, index: 23 }),
    ogrenci360: applyVisibility({ key: 'ogrenci360', label: 'Öğrenci360', icon: <PeopleAlt />, index: 50 }),
    ogrenciEtkinlikleri: { key: 'ogrenciEtkinlikleri', label: 'Öğrenci Etkinlikleri', icon: <InsightsIcon />, index: -1, path: '/admin/ogrenci-etkinlikleri' },
    puanlar: applyVisibility({ key: 'puanlar', label: 'Puanlar', icon: <BarChart />, index: -1, path: '/admin/points' }),
    okulSinavSonuclari: applyVisibility({ key: 'okulSinavSonuclari', label: 'Okul Sınav Sonuçları', icon: <AssessmentIcon />, index: 24 }),
    okulSinavOrtalamalari: applyVisibility({ key: 'okulSinavOrtalamalari', label: 'Ortalama Sıralaması', icon: <InsightsIcon />, index: 25 }),
    genelDegerlendirmeKayit: applyVisibility({ key: 'genelDegerlendirmeKayit', label: 'Ders Değerlendirmeleri', icon: <AssessmentIcon />, index: 32 }),
    genelDegerlendirmeTablo: applyVisibility({ key: 'genelDegerlendirmeTablo', label: 'Sonuç Tablosu', icon: <TimelineIcon />, index: 33 }),
    okumaHiziKayit: applyVisibility({ key: 'okumaHiziKayit', label: 'Okuma Hızı Testi', icon: <AssessmentIcon />, index: 36 }),
    okumaHiziTablo: applyVisibility({ key: 'okumaHiziTablo', label: 'Sonuç Tablosu', icon: <TimelineIcon />, index: 37 }),
    storyBookEvaluationKayit: applyVisibility({ key: 'storyBookEvaluationKayit', label: 'Sınav Kayıt', icon: <MenuBookIcon />, index: 34 }),
    storyBookEvaluationTablo: applyVisibility({ key: 'storyBookEvaluationTablo', label: 'Sonuç Tablosu', icon: <TimelineIcon />, index: 35 }),
    kitap: applyVisibility({ key: 'kitap', label: 'Kitaplar', icon: <MenuBookIcon />, index: 15 }),
    gozlemGunlugu: applyVisibility({ key: 'gozlemGunlugu', label: 'Gözlem Günlüğü', icon: <RateReviewIcon />, index: 30 }),
    gozlemOzeti: applyVisibility({ key: 'gozlemOzeti', label: 'Gözlem Özeti', icon: <InsightsIcon />, index: 31 }),
    degerTema: applyVisibility({ key: 'degerTema', label: 'Değer Temaları', icon: <ColorLensIcon />, index: 27, hidden: isTeacher }),
    degerProgram: applyVisibility({ key: 'degerProgram', label: 'Değer Programı', icon: <ScheduleSendIcon />, index: 28, hidden: false }),
    degerSonuc: applyVisibility({ key: 'degerSonuc', label: 'Değerlendirme Sonuçları', icon: <FactCheck />, index: 29, hidden: false }),
    ogretmenDersProgrami: applyVisibility({ key: 'ogretmenDersProgrami', label: 'Öğretmen Ders Programı', icon: <ScheduleIcon />, index: 11 }),
    ogretmen: applyVisibility({ key: 'ogretmen', label: 'Öğretmen Yönetimi', icon: <Person />, index: 8, hidden: isTeacher }),
    ogretmenHatirlatma: applyVisibility({ key: 'ogretmenHatirlatma', label: 'Öğretmen Hatırlatmaları', icon: <NotificationsActiveIcon />, index: 26, hidden: isTeacher }),
    veliHatirlatma: applyVisibility({ key: 'veliHatirlatma', label: 'Veli Hatırlatmaları', icon: <NotificationsActiveIcon />, index: 38, hidden: isTeacher }),
    gelenMesajlar: applyVisibility({ key: 'gelenMesajlar', label: 'Gelen Mesajlar', icon: <Chat />, index: 45, hidden: isTeacher }),
    miniGorevler: applyVisibility({ key: 'miniGorevler', label: 'Kısayol Üret', icon: <FlashOn />, index: -1, path: '/admin/mini-tasks' }),
    miniGorevAkilliTahta: applyVisibility({ key: 'miniGorevAkilliTahta', label: 'Akıllı Tahta', icon: <DesktopWindowsRoundedIcon />, index: -1, path: '/admin/mini-tasks/smartboard' }, 'miniGorevler'),
    miniGorevIstatistik: applyVisibility({ key: 'miniGorevIstatistik', label: 'İstatistikler', icon: <InsightsIcon />, index: -1, path: '/admin/mini-tasks/statistics' }, 'miniGorevler'),
    miniGorevKazanim: applyVisibility({ key: 'miniGorevKazanim', label: 'Kazanım Durumu', icon: <AssessmentIcon />, index: -1, path: '/admin/mini-tasks/outcomes' }, 'miniGorevler'),
    miniGorevIcerikQa: applyVisibility({ key: 'miniGorevIcerikQa', label: 'İçerik Değerlendirme', icon: <RateReviewIcon />, index: -1, path: '/admin/mini-tasks/content-qa' }, 'miniGorevler'),
    miniGorevOkuma: applyVisibility({ key: 'miniGorevOkuma', label: 'Haftalık Okumalar', icon: <MenuBookIcon />, index: -1, path: '/admin/mini-tasks/weekly-readings' }, 'miniGorevler'),
    gelecekOrmani: applyVisibility({ key: 'gelecekOrmani', label: 'Gelecek Ormanı', icon: <EmojiNatureIcon />, index: -1, path: '/admin/vks/gelecek-ormani' }),
    denemePlanlama: applyVisibility({ key: 'denemePlanlama', label: 'Deneme Planlama', icon: <AssessmentIcon />, index: 21, hidden: !canAccessDenemePlanlama }),
    sinavSonuclari: applyVisibility({ key: 'sinavSonuclari', label: 'Deneme Sonuçları', icon: <AssessmentIcon />, index: 19 }),
    sablonDurumu: applyVisibility({ key: 'sablonDurumu', label: 'Şablon Durumu', icon: <Chat />, index: 16, hidden: isTeacher }),
    playground: applyVisibility({ key: 'playground', label: 'Playground', icon: <Chat />, index: 17, hidden: isTeacher }),
    cetelelerim: { key: 'cetelelerim', label: 'Çetelelerim', icon: <FactCheck />, index: -1, path: '/admin/cetelelerim' },
    calismaGunlugu: applyVisibility({ key: 'calismaGunlugu', label: 'Çalışma Günlüğü', icon: <Assignment />, index: 18 }),
    mesajlar: applyVisibility({ key: 'mesajlar', label: 'Mesajlar', icon: <Chat />, index: 7, hidden: isTeacher }),
    yonetici: applyVisibility({ key: 'yonetici', label: 'Admin Yönetimi', icon: <AdminPanelSettings />, index: 9, hidden: isTeacher }),
    okulBilgi: { key: 'okulBilgi', label: 'Okul Bilgileri', icon: <SchoolIcon />, index: 10, hidden: isTeacher },
    yildizBahcem: applyVisibility({ key: 'yildizBahcem', label: 'Yıldız Bahçem', icon: <EmojiNatureIcon />, index: -1, path: '/admin/stargarden' }),
    yildizBahcemHizli: applyVisibility({ key: 'yildizBahcemHizli', label: 'Yıldız Bahçem Hızlı', icon: <FlashOn />, index: -1, path: '/admin/stargarden/batch' }),
    kutuphanem: applyVisibility({ key: 'kutuphanem', label: 'Kütüphanem', icon: <MenuBookIcon />, index: -1, path: '/admin/ogrenci-kutuphanesi/ekran' }),
    kutuphaneKitapOnaylari: applyVisibility({ key: 'kutuphaneKitapOnaylari', label: 'Kitap Onayları', icon: <FactCheck />, index: -1, path: '/admin/ogrenci-kutuphanesi/kitap-onaylari' }, 'kutuphanem'),
    kutuphaneOkumaIsaretle: applyVisibility({ key: 'kutuphaneOkumaIsaretle', label: 'Kitap Okundu İşaretle', icon: <MenuBookIcon />, index: -1, path: '/admin/ogrenci-kutuphanesi/okuma-isaretle' }),
    kutuphaneOgrenciIstatistik: applyVisibility({ key: 'kutuphaneOgrenciIstatistik', label: 'Öğrenci İstatistikleri', icon: <InsightsIcon />, index: -1, path: '/admin/ogrenci-kutuphanesi/ogrenci-istatistikleri' }),
    yildizBahcemKriter: applyVisibility({ key: 'yildizBahcemKriter', label: 'Kriter Düzenleme', icon: <SettingsIcon />, index: -1, path: '/admin/stargarden/criteria', hidden: isTeacher }),
    yildizBahcemSonuc: applyVisibility({ key: 'yildizBahcemSonuc', label: 'Kriter Sonuçları', icon: <BarChart />, index: -1, path: '/admin/stargarden/results' }),
  };

  return [
    {
      key: 'dashboard',
      label: 'Panel',
      icon: <DashboardIcon />,
      children: [items.ogretmenAnasayfa, items.genelDurum],
    },
    {
      key: 'devamsizlik',
      label: 'Devamsızlık',
      icon: <BarChart />,
      children: [items.devamsizlikYonetimi, items.devamsizlikDurumu],
    },
    {
      key: 'odevTab',
      label: 'Ödev',
      icon: <AssignmentTurnedIn />,
      children: [items.odev, items.odevDurumlari, items.odevDurumuOzeti],
    },
    {
      key: 'calisma',
      label: 'Çalışma Günlüğü',
      icon: <MenuBookIcon />,
      children: [items.calismaGunlugu],
    },
    {
      key: 'ceteleler', label: 'Çetelelerim', icon: <FactCheck />,
      children: [items.cetelelerim],
    },
    {
      key: 'miniGorevler',
      label: 'Mini Görevler',
      icon: <FlashOn />,
      children: [items.miniGorevler, items.miniGorevAkilliTahta, items.miniGorevIstatistik, items.miniGorevKazanim, items.miniGorevIcerikQa, items.miniGorevOkuma],
    },
    {
      key: 'deneme',
      label: 'Deneme',
      icon: <InsightsIcon />,
      children: [items.denemePlanlama, items.sinavSonuclari],
    },
    {
      key: 'odeme',
      label: 'Ödeme',
      icon: <PaidIcon />,
      children: [items.danduOdemeleri, items.odemePlani, items.odemeIstatistik],
    },
    {
      key: 'iletisim',
      label: 'İletişim',
      icon: <MailOutlineIcon />,
      children: [items.sablonDurumu, items.mesajlar, items.gelenMesajlar, items.playground],
    },
    {
      key: 'okul',
      label: 'Okul',
      icon: <SchoolIcon />,
      children: [items.okulBilgi],
    },
    {
      key: 'yildizBahcem',
      label: 'Yıldız Bahçem',
      icon: <EmojiNatureIcon />,
      children: [items.yildizBahcem, items.yildizBahcemHizli, items.yildizBahcemSonuc, items.yildizBahcemKriter],
    },
    {
      key: 'gelecekOrmani',
      label: 'Veli Katılım Sistemi (VKS)',
      icon: <EmojiNatureIcon />,
      children: [items.gelecekOrmani],
    },
    {
      key: 'kutuphane',
      label: 'Kütüphane',
      icon: <MenuBookIcon />,
      children: [items.kutuphanem, items.kutuphaneKitapOnaylari, items.kutuphaneOkumaIsaretle, items.kitap, items.kutuphaneOgrenciIstatistik],
    },
    {
      key: 'genelDegerlendirme',
      label: 'Ders Değerlendirmeleri',
      icon: <AssessmentIcon />,
      children: [items.genelDegerlendirmeKayit, items.genelDegerlendirmeTablo],
    },
    {
      key: 'okumaHizi',
      label: 'Okuma Hızı',
      icon: <AssessmentIcon />,
      children: [items.okumaHiziKayit, items.okumaHiziTablo],
    },
    {
      key: 'storybook',
      label: 'Hikaye Kitabı Değerlendirme',
      icon: <MenuBookIcon />,
      children: [items.storyBookEvaluationKayit, items.storyBookEvaluationTablo],
    },
    {
      key: 'gozlem',
      label: 'Gözlem',
      icon: <RateReviewIcon />,
      children: [items.gozlemGunlugu, items.gozlemOzeti],
    },
    {
      key: 'maarif',
      label: 'Öğrenme Çıktısı',
      icon: <AssessmentIcon />,
      children: [items.ogrenmeCiktisiDegerlendirme, items.ogrenmeCiktisiOzet, items.ogrenmeCiktisiOgrenciPerformans],
    },
    {
      key: 'deger',
      label: 'Değer Kataloğu',
      icon: <ColorLensIcon />,
      children: [items.degerTema, items.degerProgram, items.degerSonuc],
    },
    {
      key: 'beslenme',
      label: 'Beslenme',
      icon: <FactCheck />,
      children: [items.beslenmeYonetimi, items.beslenmeDurumu],
    },
    {
      key: 'materyal',
      label: 'Materyal',
      icon: <FactCheck />,
      children: [items.materyalYonetimi, items.materyalDurumu],
    },
    {
      key: 'okulSinavlari',
      label: 'Okul Sınavları',
      icon: <AssessmentIcon />,
      children: [items.okulSinavSonuclari, items.okulSinavOrtalamalari],
    },
    {
      key: 'yonetim',
      label: 'Yönetim',
      icon: <SettingsIcon />,
      children: [
        items.veliOgrenci,
        items.veliTanisma,
        items.ogretmen,
        items.yonetici,
        items.sinif,
        items.ders,
        items.dersProgrami,
        items.ogretmenDersProgrami,
        items.ogretmenHatirlatma,
        items.veliHatirlatma,
      ],
    },
    {
      key: 'ogrenci',
      label: 'Öğrenci',
      icon: <PeopleAlt />,
      children: [items.ogrenci360, items.ogrenciEtkinlikleri, items.puanlar],
    },
  ].map((group) => ({
    ...group,
    children: group.children.map(withDefaultPath),
  }));
};

const SidebarNav = ({
  activeIndex,
  onSelect,
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onMobileClose,
  isTeacher,
  showTeacherDenemePlanlama = false,
  featureSettings = {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboardRoute = location.pathname === '/admin' || location.pathname === '/admin/';
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const groups = useMemo(
    () => buildGroups(isTeacher, showTeacherDenemePlanlama, featureSettings, isTeacher ? 'teacher' : 'admin'),
    [featureSettings, isTeacher, showTeacherDenemePlanlama]
  );
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    groups.forEach((g) => {
      initial[g.key] = g.children.some((item) => item.path === location.pathname);
    });
    return initial;
  });

  useEffect(() => {
    const activeGroup = groups.find((group) => group.children.some((item) => item.path === location.pathname));
    if (activeGroup) {
      setOpenGroups((current) => current[activeGroup.key] ? current : { ...current, [activeGroup.key]: true });
    }
  }, [groups, location.pathname]);

  const handleToggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const content = (
    <Box sx={{ width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH, minHeight: '100%', bgcolor: '#fff' }}>
      <Toolbar sx={{ minHeight: collapsed ? 78 : 72, px: collapsed ? 1 : 1.5, display: 'flex', flexDirection: collapsed ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', gap: collapsed ? 0.5 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.15, minWidth: 0 }}>
          <Box aria-hidden="true" sx={{ width: collapsed ? 34 : 38, height: collapsed ? 29 : 32, display: 'flex', flex: '0 0 auto', gap: '3px' }}>
            <Box component="span" sx={{ flex: 1, borderRadius: '7px 3px 4px 7px', background: 'linear-gradient(145deg, #1692ff, #075ce8)', boxShadow: 'inset -2px 0 0 rgba(255,255,255,.25)' }} />
            <Box component="span" sx={{ flex: 1, borderRadius: '3px 7px 7px 4px', background: 'linear-gradient(145deg, #1692ff, #075ce8)', boxShadow: 'inset 2px 0 0 rgba(255,255,255,.22)' }} />
          </Box>
          {!collapsed && <Box sx={{ minWidth: 0 }}><Box sx={{ color: '#0a1744', fontSize: 17, lineHeight: 1.1, fontWeight: 900, letterSpacing: '.2px' }}>DANDU</Box><Box sx={{ mt: 0.35, color: '#667592', fontSize: 10.5 }}>Yönetim</Box></Box>}
        </Box>
        {isMdUp && (
          <IconButton size="small" onClick={onToggleCollapse} aria-label={collapsed ? 'Genişlet' : 'Daralt'} sx={{ color: '#6a7894', bgcolor: '#f5f8fc', '&:hover': { bgcolor: '#eaf3ff', color: '#0875f5' } }}>
            {theme.direction === 'rtl'
              ? collapsed
                ? <ChevronRight />
                : <ChevronLeft />
              : collapsed
                ? <ChevronLeft />
                : <ChevronRight />}
          </IconButton>
        )}
      </Toolbar>
      <Divider sx={{ borderColor: '#edf2f8' }} />
      <List disablePadding sx={{ py: 1 }}>
        {groups.map((group) => {
          const visibleChildren = group.children.filter((item) => !item?.hidden);
          if (visibleChildren.length === 0) return null;
          const groupSelected = visibleChildren.some((item) => item.path ? item.path === location.pathname : isDashboardRoute && activeIndex === item.index);
          const groupHeader = (
            <ListSubheader
              component="div"
              disableSticky
              sx={{
                lineHeight: 2.5,
                bgcolor: 'transparent',
                color: 'text.secondary',
                px: 2,
                ...(collapsed ? { px: 1.5, textAlign: 'center' } : {}),
              }}
            >
              {collapsed ? group.label[0] : group.label}
            </ListSubheader>
          );

          return (
            <Box key={group.key} sx={{ pb: 0.35 }}>
              {!collapsed && (
                <ListItemButton onClick={() => handleToggleGroup(group.key)} sx={{ minHeight: 46, py: 0.8, mx: 1, my: 0.2, borderRadius: 2.5, color: groupSelected ? '#0875f5' : '#3c4965', bgcolor: groupSelected ? '#f0f7ff' : 'transparent', '&:hover': { bgcolor: '#f4f8fd', color: '#0875f5' } }}>
                  <ListItemIcon sx={{ minWidth: 38, color: groupSelected ? '#0875f5' : '#6a7894', '& .MuiSvgIcon-root': { fontSize: 20 } }}>{group.icon}</ListItemIcon>
                  {!collapsed && <ListItemText primary={group.label} primaryTypographyProps={{ fontSize: 14, fontWeight: groupSelected ? 800 : 650 }} />}
                  {openGroups[group.key] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              )}
              {collapsed && groupHeader}

              <Collapse in={openGroups[group.key] || collapsed} timeout="auto" unmountOnExit={false}>
                {visibleChildren.map((item) => {
                  const selected = item.path ? item.path === location.pathname : isDashboardRoute && activeIndex === item.index;
                  const button = (
                    <ListItemButton
                      selected={selected}
                      aria-current={selected ? 'page' : undefined}
                      onClick={() => {
                        if (item.path) {
                          navigate(item.path);
                        } else {
                          onSelect(item.index);
                        }
                        if (!isMdUp) onMobileClose?.();
                      }}
                      sx={{
                        minHeight: item.key === 'ogrenciEtkinlikleri' ? 44 : 40,
                        py: 0.65,
                        pl: collapsed ? 1.4 : 2.2,
                        borderRadius: 2.25,
                        mx: collapsed ? 0.8 : 1.25,
                        my: 0.25,
                        color: selected ? '#0875f5' : '#607091',
                        '&.Mui-selected': { bgcolor: '#e7f3ff', color: '#0875f5' },
                        '&.Mui-selected:hover': { bgcolor: '#dcedff' },
                        '&:hover': { bgcolor: '#f2f8ff', color: '#0875f5' },
                        ...(item.key === 'ogrenciEtkinlikleri' ? {
                          '&:hover': { bgcolor: '#f2f8ff', color: '#1E40AF' },
                          '&.Mui-focusVisible': { outline: '3px solid #1E40AF', outlineOffset: -3 },
                          '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#e7f3ff', color: '#1E40AF' },
                          '&.Mui-selected .MuiListItemIcon-root, &.Mui-selected .MuiListItemText-primary': { color: '#1E40AF' },
                        } : {}),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 34, color: 'inherit', '& .MuiSvgIcon-root': { fontSize: 18 } }}>{item.icon}</ListItemIcon>
                      {!collapsed && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13, lineHeight: 1.15, fontWeight: selected ? 800 : 650 }} />}
                    </ListItemButton>
                  );
                  return (
                    <Box key={item.key}>
                      {collapsed ? (
                        <Tooltip title={item.label} placement="right">
                          {button}
                        </Tooltip>
                      ) : (
                        button
                      )}
                    </Box>
                  );
                })}
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      {/* Mobile temporary drawer */}
      <Drawer
        variant="temporary"
        open={!!mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH, borderRight: '1px solid #e8eef6', bgcolor: '#fff' },
        }}
      >
        {content}
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            borderRight: '1px solid #e8eef6',
            bgcolor: '#fff',
            width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        {content}
      </Drawer>
    </>
  );
};

export default SidebarNav;
